/**
 * SyncStatusPill — small reusable indicator for local-first wellness flows.
 *
 * Subscribes to a local-first queue and renders one of four honest states:
 *   - Offline mode active
 *   - N update(s) queued to sync
 *   - Synced (recently)
 *   - Sync paused (with retry CTA)
 *
 * Deliberately compact — designed to sit inside a page header or above a
 * primary action button without taking visual weight.
 */

import React, { useEffect, useState } from 'react';
import { WifiOff, Clock, CheckCircle2, PauseCircle, RefreshCw } from 'lucide-react';
import { QUEUE_STATUS, isOnline } from '@/services/localFirstQueue';

export function SyncStatusPill({ queue, className = '', onRetry, 'data-testid': testId }) {
  const [snap, setSnap] = useState(() => ({ status: queue.getStatus(), size: queue.size() }));
  const [online, setOnline] = useState(() => isOnline());

  useEffect(() => {
    const unsub = queue.subscribe(setSnap);
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      unsub();
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [queue]);

  // Hide the pill entirely when there's nothing meaningful to say —
  // synced + empty is the default state and doesn't need shouting about.
  if (online && snap.size === 0 && snap.status === QUEUE_STATUS.ONLINE_SYNCED) {
    return null;
  }

  let Icon = CheckCircle2;
  let label = 'Synced';
  let tone = 'border-emerald-200 bg-emerald-50 text-emerald-800';

  if (!online) {
    Icon = WifiOff;
    label = snap.size > 0
      ? `Offline · ${snap.size} update${snap.size === 1 ? '' : 's'} queued`
      : 'Offline mode active';
    tone = 'border-slate-200 bg-slate-50 text-slate-700';
  } else if (snap.status === QUEUE_STATUS.FLUSHING) {
    Icon = RefreshCw;
    label = 'Syncing your updates…';
    tone = 'border-teal-200 bg-teal-50 text-teal-800';
  } else if (snap.status === QUEUE_STATUS.SYNC_PAUSED) {
    Icon = PauseCircle;
    label = snap.size > 0
      ? `Sync paused · ${snap.size} pending`
      : 'Sync paused';
    tone = 'border-amber-200 bg-amber-50 text-amber-800';
  } else if (snap.size > 0) {
    Icon = Clock;
    label = `${snap.size} update${snap.size === 1 ? '' : 's'} queued`;
    tone = 'border-slate-200 bg-slate-50 text-slate-700';
  }

  const showRetry = snap.status === QUEUE_STATUS.SYNC_PAUSED && online;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium ${tone} ${className}`}
      data-testid={testId || 'sync-status-pill'}
    >
      <Icon className={`h-3.5 w-3.5 ${snap.status === QUEUE_STATUS.FLUSHING ? 'animate-spin' : ''}`} aria-hidden="true" />
      <span>{label}</span>
      {showRetry && (
        <button
          type="button"
          onClick={() => (onRetry ? onRetry() : queue.flush())}
          className="ml-1 underline underline-offset-2 hover:opacity-80"
          data-testid="sync-status-pill-retry"
        >
          Retry
        </button>
      )}
    </div>
  );
}

export default SyncStatusPill;
